/**
 * 1stbuddha — facets.js
 *
 * <collection-facets> (sections/collection.liquid) filters, sorts and pages a
 * collection in place with the Section Rendering API:
 *   - any change inside a [data-facet-form] (sidebar or drawer), the toolbar
 *     sort select (tied to the sidebar form with form="…") or the mobile sort
 *     select re-renders the section for the new query string
 *   - [data-facet-link] links (remove a filter, clear all, empty-state
 *     buttons) do the same with their href
 *   - regions marked [data-facets-region="name"] are swapped with the fresh
 *     markup; open filter groups, "Show more" lists, drawer scroll position and
 *     focus are kept
 *   - the URL follows (pushState; back/forward re-render), results fade to
 *     50% while loading (150 ms), and a polite live region announces the count
 *   - [data-load-more] appends the next page, moves focus to the first new
 *     card and updates ?page= with replaceState
 *   - [data-filters-toggle] hides/shows the desktop sidebar (4-column grid)
 *   - the drawer's "Show 28 pieces" submit closes the drawer (results are
 *     already live)
 * Without JS the forms submit normally and "Load more" is a link to page 2.
 *
 * <price-range> keeps the two range thumbs and the min/max fields in sync and
 * reports a change once a thumb is released or a field is committed.
 */
(() => {
  if (customElements.get('collection-facets')) return;

  const parser = new DOMParser();

  class CollectionFacets extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map();
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onPopState = this.onPopState.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
      window.addEventListener('popstate', this.onPopState);
      this.restoreSidebarState();
      // The first page is cached so going back to it doesn't refetch.
      this.initialQuery = window.location.search.slice(1);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      this.controller?.abort();
    }

    /* ---------- Events ---------- */

    onChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.matches('[data-range-min], [data-range-max]')) return; // <price-range> reports its own change

      if (target.matches('[data-sort-mobile]')) {
        const params = new URLSearchParams(window.location.search);
        params.set('sort_by', target.value);
        if (target.value === this.dataset.defaultSort) params.delete('sort_by');
        params.delete('page');
        this.render(params.toString());
        return;
      }

      const form = target.form?.matches('[data-facet-form]') ? target.form : target.closest('[data-facet-form]');
      if (!form) return;
      this.render(this.queryFromForm(form));
    }

    onSubmit(event) {
      const form = event.target.closest('[data-facet-form]');
      if (!form) return;
      event.preventDefault();
      const query = this.queryFromForm(form);
      if (query !== window.location.search.slice(1)) this.render(query);
      this.querySelector('dialog-component')?.close();
    }

    onClick(event) {
      const link = event.target.closest('a[data-facet-link]');
      if (link && this.contains(link)) {
        event.preventDefault();
        this.render(new URL(link.href, window.location.href).searchParams.toString());
        return;
      }

      const more = event.target.closest('[data-load-more]');
      if (more) {
        event.preventDefault();
        this.loadMore(more);
        return;
      }

      const toggle = event.target.closest('[data-filters-toggle]');
      if (toggle) {
        this.setSidebarHidden(this.dataset.filtersHidden !== 'true');
        return;
      }

      const showMore = event.target.closest('[data-show-more]');
      if (showMore) {
        const list = document.getElementById(showMore.getAttribute('aria-controls'));
        if (!list) return;
        const expanded = list.dataset.expanded !== 'true';
        list.dataset.expanded = String(expanded);
        showMore.setAttribute('aria-expanded', String(expanded));
        showMore.textContent = expanded ? showMore.dataset.labelLess : showMore.dataset.labelMore;
      }
    }

    onPopState() {
      this.render(window.location.search.slice(1), { push: false });
    }

    /* ---------- Query ---------- */

    queryFromForm(form) {
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(form)) {
        if (typeof value === 'string' && value.trim() !== '') params.append(key, value.trim());
      }
      if (!params.has('sort_by')) {
        const current = new URLSearchParams(window.location.search).get('sort_by');
        if (current) params.set('sort_by', current);
      }
      // Keep URLs canonical: the collection's own default sort isn't written out.
      if (params.get('sort_by') === this.dataset.defaultSort) params.delete('sort_by');
      return params.toString();
    }

    /* ---------- Rendering ---------- */

    async fetchSection(query) {
      if (this.cache.has(query)) return this.cache.get(query);

      this.controller?.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams(query);
      params.set('section_id', this.sectionId);
      const response = await fetch(`${window.location.pathname}?${params}`, { signal: this.controller.signal });
      if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
      const html = await response.text();
      this.cache.set(query, html);
      return html;
    }

    async render(query, { push = true } = {}) {
      const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
      this.setBusy(true);
      try {
        const html = await this.fetchSection(query);
        const next = parser.parseFromString(html, 'text/html').querySelector('collection-facets');
        if (!next) throw new Error('Missing <collection-facets> in section response');
        this.swap(next);
        if (push) window.history.pushState({ facets: true }, '', url);
        this.announce();
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.assign(url); // fall back to a full page load
      } finally {
        this.setBusy(false);
      }
    }

    swap(next) {
      const openGroups = new Map();
      this.querySelectorAll('[data-facet-group]').forEach((group) => {
        openGroups.set(this.groupKey(group), group.dataset.open);
      });
      const expandedLists = new Set(
        [...this.querySelectorAll('[data-facet-values][data-expanded="true"]')].map((list) => list.id)
      );
      const scrollTops = new Map(
        [...this.querySelectorAll('[data-facets-scroll]')].map((el) => [el.closest('form')?.id, el.scrollTop])
      );
      const activeId = this.contains(document.activeElement) ? document.activeElement.id : null;

      this.querySelectorAll('[data-facets-region]').forEach((region) => {
        if (region.parentElement?.closest('[data-facets-region]')) return; // nested: swapped with its parent
        const fresh = next.querySelector(`[data-facets-region="${region.dataset.facetsRegion}"]`);
        if (fresh) region.replaceWith(fresh);
      });

      this.querySelectorAll('[data-facet-group]').forEach((group) => {
        const open = openGroups.get(this.groupKey(group));
        if (open === undefined) return;
        group.dataset.open = open;
        group.querySelector('.accordion__trigger')?.setAttribute('aria-expanded', open);
      });
      expandedLists.forEach((id) => {
        const list = document.getElementById(id);
        const button = this.querySelector(`[data-show-more][aria-controls="${id}"]`);
        if (!list || !button) return;
        list.dataset.expanded = 'true';
        button.setAttribute('aria-expanded', 'true');
        button.textContent = button.dataset.labelLess;
      });
      this.querySelectorAll('[data-facets-scroll]').forEach((el) => {
        const top = scrollTops.get(el.closest('form')?.id);
        if (top) el.scrollTop = top;
      });
      if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });

      window.themeWishlist?.sync(this);
    }

    groupKey(group) {
      return `${group.closest('form')?.id}|${group.dataset.facetGroup}`;
    }

    setBusy(busy) {
      this.classList.toggle('is-loading', busy);
      this.querySelector('[data-facets-region="results"]')?.setAttribute('aria-busy', String(busy));
    }

    announce() {
      const status = this.querySelector('[data-facets-status]');
      const count = this.querySelector('[data-facets-region="count-toolbar"]');
      if (status && count) status.textContent = count.textContent.trim();
    }

    /* ---------- Load more ---------- */

    async loadMore(link) {
      if (link.getAttribute('aria-busy') === 'true') return;
      link.setAttribute('aria-busy', 'true');
      const query = new URL(link.href, window.location.href).searchParams.toString();

      try {
        const html = await this.fetchSection(query);
        const next = parser.parseFromString(html, 'text/html');
        const grid = this.querySelector('[data-product-grid]');
        const items = [...next.querySelectorAll('[data-product-grid] > [data-product-item]')];
        if (!grid || items.length === 0) throw new Error('No products in next page');

        grid.append(...items);
        const region = this.querySelector('[data-facets-region="load-more"]');
        const fresh = next.querySelector('[data-facets-region="load-more"]');
        if (region && fresh) region.replaceWith(fresh);
        else region?.remove();

        window.history.replaceState({ facets: true }, '', `${window.location.pathname}?${query}`);
        window.themeWishlist?.sync(grid);
        items[0].querySelector('a')?.focus({ preventScroll: false });

        const status = this.querySelector('[data-facets-status]');
        const label = this.querySelector('.collection__progress-label');
        if (status && label) status.textContent = label.textContent.trim();
      } catch (error) {
        if (error.name !== 'AbortError') window.location.assign(link.href);
      } finally {
        link.removeAttribute('aria-busy');
      }
    }

    /* ---------- Sidebar toggle (desktop) ---------- */

    setSidebarHidden(hidden) {
      this.dataset.filtersHidden = String(hidden);
      const toggle = this.querySelector('[data-filters-toggle]');
      if (toggle) {
        toggle.setAttribute('aria-expanded', String(!hidden));
        toggle.querySelector('[data-label-when="shown"]')?.toggleAttribute('hidden', hidden);
        toggle.querySelector('[data-label-when="hidden"]')?.toggleAttribute('hidden', !hidden);
      }
      try {
        window.sessionStorage.setItem('facets:sidebar-hidden', String(hidden));
      } catch {
        /* storage unavailable: the choice just isn't remembered */
      }
    }

    restoreSidebarState() {
      let hidden = false;
      try {
        hidden = window.sessionStorage.getItem('facets:sidebar-hidden') === 'true';
      } catch {
        hidden = false;
      }
      if (hidden) this.setSidebarHidden(true);
    }
  }

  class PriceRange extends HTMLElement {
    connectedCallback() {
      this.max = Number(this.dataset.max) || 0;
      this.sliderMin = this.querySelector('[data-range-min]');
      this.sliderMax = this.querySelector('[data-range-max]');
      this.fieldMin = this.querySelector('[data-price-min]');
      this.fieldMax = this.querySelector('[data-price-max]');
      this.track = this.querySelector('.price-range__track');
      if (!this.sliderMin || !this.sliderMax || !this.fieldMin || !this.fieldMax) return;

      this.sliderMin.addEventListener('input', () => this.fromSlider(this.sliderMin));
      this.sliderMax.addEventListener('input', () => this.fromSlider(this.sliderMax));
      // Commit when a thumb is released (change) — the field reports it.
      [this.sliderMin, this.sliderMax].forEach((slider) =>
        slider.addEventListener('change', () => {
          const field = slider === this.sliderMin ? this.fieldMin : this.fieldMax;
          field.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
      [this.fieldMin, this.fieldMax].forEach((field) => field.addEventListener('input', () => this.fromFields()));
      this.fromFields();
    }

    fromSlider(slider) {
      let low = Number(this.sliderMin.value);
      let high = Number(this.sliderMax.value);
      if (low > high) {
        if (slider === this.sliderMin) low = high;
        else high = low;
        this.sliderMin.value = String(low);
        this.sliderMax.value = String(high);
      }
      this.fieldMin.value = low > 0 ? String(low) : '';
      this.fieldMax.value = high < this.max ? String(high) : '';
      this.paint(low, high);
    }

    fromFields() {
      const low = this.fieldMin.value === '' ? 0 : Math.max(0, Number(this.fieldMin.value));
      const high = this.fieldMax.value === '' ? this.max : Math.min(this.max, Number(this.fieldMax.value));
      this.sliderMin.value = String(Math.min(low, high));
      this.sliderMax.value = String(Math.max(low, high));
      this.paint(Math.min(low, high), Math.max(low, high));
    }

    paint(low, high) {
      if (!this.track || !this.max) return;
      this.track.style.setProperty('--from', `${(low / this.max) * 100}%`);
      this.track.style.setProperty('--to', `${(high / this.max) * 100}%`);
    }
  }

  customElements.define('collection-facets', CollectionFacets);
  if (!customElements.get('price-range')) customElements.define('price-range', PriceRange);
})();
